import type { InvoiceRevenueMonth } from "@/lib/heroDocumentReports";
import type { MahnlaufItem } from "@/lib/mahnwesen";
import type { OffenePostenSummary } from "@/lib/offenePosten";

export type BuchhaltungTaskTone = "danger" | "warning" | "info" | "neutral";

export interface BuchhaltungTask {
  key: string;
  label: string;
  count: number;
  hint: string;
  href: string;
  tone: BuchhaltungTaskTone;
}

export interface BuchhaltungTaskCounts {
  mahnungen: number;
  zuVersenden: number;
  entwuerfe: number;
  nachweiseAbzurechnen: number;
  neueLieferscheine: number;
  rueckfragen: number;
}

/**
 * Aufgabenliste für „Heute zu erledigen“ – Reihenfolge nach Dringlichkeit,
 * erledigte Punkte (0) rutschen ans Ende, bleiben aber sichtbar.
 */
export function buildBuchhaltungTasks(counts: BuchhaltungTaskCounts): BuchhaltungTask[] {
  const tasks: BuchhaltungTask[] = [
    {
      key: "mahnungen",
      label: "Mahnungen fällig",
      count: counts.mahnungen,
      hint: "Überfällige Rechnungen, nächste Mahnstufe steht an",
      href: "/dokumente/mahnwesen",
      tone: "danger",
    },
    {
      key: "versand",
      label: "Rechnungen versenden",
      count: counts.zuVersenden,
      hint: "Erstellt, aber noch nicht an den Kunden gegangen",
      href: "/dokumente/rechnungen?status=erstellt",
      tone: "warning",
    },
    {
      key: "nachweise",
      label: "Nachweise abrechnen",
      count: counts.nachweiseAbzurechnen,
      hint: "Unterschriebene Stundennachweise ohne Rechnung",
      href: "/dokumente/stundennachweise?status=signed",
      tone: "warning",
    },
    {
      key: "lieferscheine",
      label: "Neue Lieferscheine",
      count: counts.neueLieferscheine,
      hint: "Gelieferte Positionen, noch nicht geprüft",
      href: "/dokumente/lieferscheine",
      tone: "info",
    },
    {
      key: "rueckfragen",
      label: "Offene Rückfragen",
      count: counts.rueckfragen,
      hint: "Stundennachweise mit unbeantworteter Rückfrage",
      href: "/dokumente/stundennachweise?rueckfrage=1",
      tone: "info",
    },
    {
      key: "entwuerfe",
      label: "Rechnungsentwürfe",
      count: counts.entwuerfe,
      hint: "Noch nicht als Rechnung erstellt",
      href: "/dokumente/rechnungen?status=entwurf",
      tone: "neutral",
    },
  ];
  return tasks;
}

export function totalOpenTasks(tasks: BuchhaltungTask[]): number {
  return tasks.reduce((sum, task) => sum + task.count, 0);
}

/** Mahnbare Posten – die ältesten und größten zuerst. */
export function sortMahnbar(items: MahnlaufItem[]): MahnlaufItem[] {
  return [...items].sort((a, b) => {
    if (b.tage_ueberfaellig !== a.tage_ueberfaellig) {
      return b.tage_ueberfaellig - a.tage_ueberfaellig;
    }
    return (b.summe_offen ?? 0) - (a.summe_offen ?? 0);
  });
}

/** Anteil überfälliger Beträge am offenen Gesamtbetrag (0–1). */
export function overdueShare(summary: OffenePostenSummary | null | undefined): number {
  if (!summary || !(summary.open_amount > 0)) return 0;
  return Math.min(1, Math.max(0, summary.overdue_amount / summary.open_amount));
}

export type LageTone = "good" | "attention" | "critical";

/**
 * Ampel für die Gesamtlage: kritisch ab 40 % überfälligem Anteil oder Posten
 * älter als 60 Tage mit nennenswertem Betrag, Aufmerksamkeit ab 15 %.
 */
export function lageTone(summary: OffenePostenSummary | null | undefined): LageTone {
  if (!summary || !(summary.open_amount > 0)) return "good";
  const share = overdueShare(summary);
  const severe = summary.buckets?.d61_plus?.amount ?? 0;
  if (share >= 0.4 || (severe > 0 && severe / summary.open_amount >= 0.2)) return "critical";
  if (share >= 0.15 || summary.overdue_count > 0) return "attention";
  return "good";
}

export function lageLabel(tone: LageTone): string {
  if (tone === "critical") return "Handlungsbedarf";
  if (tone === "attention") return "Im Blick behalten";
  return "Alles im grünen Bereich";
}

export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function findMonth(
  rows: InvoiceRevenueMonth[] | null | undefined,
  key: string,
): InvoiceRevenueMonth | null {
  if (!rows?.length) return null;
  return rows.find((row) => String(row.month).startsWith(key)) ?? null;
}

export function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}
